import { useState, useEffect } from 'react';
import { ProductCategory, PricingMethod } from '@/types';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { Calculator, Banknote, RefreshCw } from 'lucide-react';

interface CategoryFormData {
  name: string;
  description: string;
  pricing_method: PricingMethod;
  cash_multiplier: number;
  card_multiplier: number;
  transfer_multiplier: number;
  qr_multiplier: number;
  price_round_to: number;
  margin_min: number | '';
  margin_target: number | '';
}

const DEFAULT_FORM_DATA: CategoryFormData = {
  name: '',
  description: '',
  pricing_method: 'multiplier',
  cash_multiplier: 2.0,
  card_multiplier: 2.2,
  transfer_multiplier: 2.0,
  qr_multiplier: 2.0,
  price_round_to: 0,
  margin_min: '',
  margin_target: '',
};

interface CategoryFormModalProps {
  category?: ProductCategory | null;
  onSave: (data: Partial<CategoryFormData>) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
  onRecalculate?: (categoryId: number) => Promise<void>;
  isRecalculating?: boolean;
}

// Calcula precio segun pricing_method y redondeo.
// - multiplier: cost × value
// - margin: cost / (1 − value/100)
function calcPrice(cost: number, value: number, method: PricingMethod, roundTo: number): number {
  if (cost <= 0 || value <= 0) return 0;
  let price: number;
  if (method === 'margin') {
    const denom = 1 - value / 100;
    if (denom <= 0) return 0;
    price = cost / denom;
  } else {
    // multiplier (default)
    price = cost * value;
  }
  if (roundTo && roundTo > 0) {
    price = Math.ceil(price / roundTo) * roundTo;
  } else {
    price = Math.round(price * 100) / 100;
  }
  return price;
}

// Convierte un valor de markup (percentage) al multiplicador equivalente.
// Ej: percentage 100 → multiplier 2; percentage 50 → multiplier 1.5
function percentageToMultiplier(percentageValue: number): number {
  return 1 + (percentageValue || 0) / 100;
}

// Dado costo y precio, devuelve los 3 equivalentes para mostrar en UI.
function priceEquivalents(cost: number, price: number) {
  if (cost <= 0 || price <= 0) return { multiplier: 0, markup: 0, margin: 0 };
  const multiplier = price / cost;
  const markup = ((price - cost) / cost) * 100;
  const margin = ((price - cost) / price) * 100;
  return { multiplier, markup, margin };
}

export default function CategoryFormModal({
  category,
  onSave,
  onClose,
  isLoading,
  onRecalculate,
  isRecalculating,
}: CategoryFormModalProps) {
  const [formData, setFormData] = useState<CategoryFormData>(DEFAULT_FORM_DATA);

  useEffect(() => {
    if (category) {
      // Si la categoria estaba guardada como Markup % (legacy), auto-convertir a multiplicador.
      const storedMethod = (category.pricing_method as PricingMethod) || 'multiplier';
      const method: PricingMethod = storedMethod === 'percentage' ? 'multiplier' : storedMethod;
      const convert = (v: number | undefined) => {
        const num = Number(v ?? 0);
        return storedMethod === 'percentage' ? percentageToMultiplier(num) : num;
      };
      setFormData({
        name: category.name,
        description: category.description || '',
        pricing_method: method,
        cash_multiplier: convert(category.cash_multiplier ?? 2.0),
        card_multiplier: convert(category.card_multiplier ?? 2.2),
        transfer_multiplier: convert(category.transfer_multiplier ?? 2.0),
        qr_multiplier: convert(category.qr_multiplier ?? 2.0),
        price_round_to: Number(category.price_round_to ?? 0),
        margin_min: category.margin_min != null ? Number(category.margin_min) : '',
        margin_target: category.margin_target != null ? Number(category.margin_target) : '',
      });
    } else {
      setFormData(DEFAULT_FORM_DATA);
    }
  }, [category]);

  const buildPayload = () => ({
    name: formData.name,
    description: formData.description || undefined,
    pricing_method: formData.pricing_method,
    // Precio único: las 4 columnas comparten el mismo multiplicador
    cash_multiplier: formData.cash_multiplier,
    card_multiplier: formData.cash_multiplier,
    transfer_multiplier: formData.cash_multiplier,
    qr_multiplier: formData.cash_multiplier,
    price_round_to: formData.price_round_to,
    margin_min: formData.margin_min === '' ? null : Number(formData.margin_min),
    margin_target: formData.margin_target === '' ? null : Number(formData.margin_target),
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(buildPayload() as any);
    onClose();
  };

  const exampleCost = 1000;
  const exampleCash = calcPrice(exampleCost, formData.cash_multiplier, formData.pricing_method, formData.price_round_to);

  const valueSuffix = formData.pricing_method === 'margin' ? '%' : '×';
  const valueHint =
    formData.pricing_method === 'margin'
      ? 'Ej: 50 = querés ganar 50% sobre cada venta (precio = costo / 0,5 = ×2)'
      : 'Ej: 2 = costo × 2 (vendés al doble)';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <Input
        label="Nombre *"
        value={formData.name}
        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
        placeholder="Ej: Almacén, Bebidas, Limpieza..."
        required
      />
      <div>
        <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-1.5">
          Descripción
        </label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          rows={2}
          className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors resize-none"
          placeholder="Descripción opcional..."
        />
      </div>

      {/* Pricing method */}
      <div className="border-t border-gray-100 pt-4">
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="h-4 w-4 text-taupe-600" />
          <h3 className="font-semibold text-gray-900">Cálculo de precios de venta</h3>
        </div>

        {/* Toggle método: 2 opciones (multiplicador / margen%) */}
        <div className="mb-4">
          <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-2">Tipo de cálculo</label>
          <div className="flex gap-1 p-1 bg-cream-100 rounded-xl">
            {([
              { value: 'multiplier', label: 'Multiplicador (×)' },
              { value: 'margin', label: 'Margen (%)' },
            ] as const).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFormData({ ...formData, pricing_method: opt.value })}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  formData.pricing_method === opt.value
                    ? 'bg-white text-taupe-800 shadow-sm'
                    : 'text-taupe-500 hover:text-taupe-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-taupe-500 mt-1.5">{valueHint}</p>
        </div>

        {/* Multiplicador único */}
        <div className="grid grid-cols-1 gap-3 mb-4">
          <PriceMultiplierInput
            icon={<Banknote className="h-4 w-4 text-emerald-600" />}
            label="Precio de venta"
            value={formData.cash_multiplier}
            suffix={valueSuffix}
            example={exampleCash}
            onChange={(v) => setFormData({ ...formData, cash_multiplier: v })}
          />
        </div>

        {/* Redondeo */}
        <div className="mb-3">
          <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-1.5">
            Redondeo
          </label>
          <select
            value={formData.price_round_to}
            onChange={(e) => setFormData({ ...formData, price_round_to: parseInt(e.target.value) })}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400 transition-colors"
          >
            <option value={0}>Sin redondeo</option>
            <option value={1}>$1</option>
            <option value={10}>$10</option>
            <option value={50}>$50</option>
            <option value={100}>$100</option>
            <option value={500}>$500</option>
            <option value={1000}>$1.000</option>
          </select>
        </div>

        {/* Margenes objetivo / minimo (alertas y repricing) */}
        <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-1.5">
              Margen mín (%)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={formData.margin_min}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  margin_min: e.target.value === '' ? '' : parseFloat(e.target.value),
                })
              }
              onFocus={(e) => e.target.select()}
              placeholder="ej: 30"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400"
            />
            <p className="text-[11px] text-taupe-500 mt-1">Dispara alerta si el margen baja.</p>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide font-semibold text-taupe-600 mb-1.5">
              Margen objetivo (%)
            </label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={formData.margin_target}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  margin_target: e.target.value === '' ? '' : parseFloat(e.target.value),
                })
              }
              onFocus={(e) => e.target.select()}
              placeholder="ej: 50"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-taupe-400 focus:border-taupe-400"
            />
            <p className="text-[11px] text-taupe-500 mt-1">Usado para sugerir precios.</p>
          </div>
        </div>

        {/* Preview compacto */}
        <div className="bg-gradient-to-br from-cream-50 to-taupe-50 rounded-xl p-3 border border-taupe-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wide font-semibold text-taupe-600">
              Ejemplo con costo $1.000
            </p>
            <p className="text-[11px] text-taupe-500">
              Aplicando: <strong className="text-taupe-700">{formData.pricing_method === 'margin' ? 'Margen %' : 'Multiplicador'}</strong>
            </p>
          </div>
          <div className="space-y-1.5">
            {[
              { icon: <Banknote className="h-3 w-3 text-emerald-600" />, label: 'Precio de venta', price: exampleCash },
            ].map((row) => {
              const eq = priceEquivalents(exampleCost, row.price);
              // Mostramos SOLO la metrica equivalente (la que NO se está aplicando) como info
              const equivLabel =
                formData.pricing_method === 'margin'
                  ? `≈ ×${eq.multiplier.toFixed(2)}`
                  : `≈ margen ${eq.margin.toFixed(0)}%`;
              return (
                <div
                  key={row.label}
                  className="flex items-center justify-between gap-2 bg-white/70 rounded-lg px-3 py-1.5 text-sm"
                >
                  <span className="text-taupe-700 inline-flex items-center gap-1.5 font-medium min-w-0 truncate">
                    {row.icon}
                    <span className="truncate">{row.label}</span>
                  </span>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="font-bold text-taupe-900 tabular-nums">
                      ${row.price.toLocaleString('es-AR')}
                    </span>
                    <span className="text-[11px] text-taupe-400 tabular-nums whitespace-nowrap italic">
                      {equivLabel}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Botón aplicar a productos existentes */}
        {category && onRecalculate && (
          <button
            type="button"
            onClick={async () => {
              await onSave(buildPayload() as any);
              await onRecalculate(category.id);
              onClose();
            }}
            disabled={isRecalculating || isLoading}
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRecalculating ? 'animate-spin' : ''}`} />
            {isRecalculating ? 'Recalculando...' : 'Guardar y aplicar a todos los productos de esta categoría'}
          </button>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button type="submit" loading={isLoading}>{category ? 'Guardar' : 'Crear'}</Button>
      </div>
    </form>
  );
}

function PriceMultiplierInput({
  icon,
  label,
  value,
  suffix,
  example,
  onChange,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  suffix: string;
  example: number;
  onChange: (v: number) => void;
}) {
  // Draft local para permitir borrar/editar libremente sin que se pise con el value externo.
  const [draft, setDraft] = useState<string>(String(value ?? ''));

  // Sincronizar cuando cambia el value desde afuera (ej. al cargar otra categoria)
  // pero solo si la representacion no coincide ya con lo tipeado.
  useEffect(() => {
    const parsed = parseFloat(draft);
    if (isNaN(parsed) || parsed !== value) {
      setDraft(String(value ?? ''));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDraft(raw);
    if (raw === '' || raw === '.' || raw === ',') {
      // Mientras escribe, no commitear (mantiene el ultimo valor valido).
      return;
    }
    const parsed = parseFloat(raw.replace(',', '.'));
    if (!isNaN(parsed) && parsed >= 0) {
      onChange(parsed);
    }
  };

  const handleBlur = () => {
    const parsed = parseFloat(draft.replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      // Restaurar al ultimo valor valido
      setDraft(String(value ?? ''));
    } else {
      // Normalizar formato
      setDraft(String(parsed));
      onChange(parsed);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-3 hover:border-taupe-200 transition-colors">
      <div className="flex items-center gap-1.5 mb-1.5">
        {icon}
        <span className="text-xs font-semibold text-taupe-700 uppercase tracking-wide">{label}</span>
      </div>
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          value={draft}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={(e) => e.target.select()}
          className="w-full pl-3 pr-8 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-taupe-400"
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
          {suffix}
        </span>
      </div>
      <p className="text-xs text-taupe-600 mt-1">→ ${example.toLocaleString('es-AR')}</p>
    </div>
  );
}
